/**
 * Downloading a table as CSV or Excel.
 *
 * One module, used by every page that offers the buttons, because the moment
 * there are two of these they start disagreeing about how a euro amount or an
 * empty cell is written - and the whole point of the download is that the
 * figures match what was on screen.
 *
 * No dependency. A spreadsheet library would add about a megabyte to a bundle
 * that contractors open on their phones, to write a table of at most a few
 * hundred rows. An .xlsx is a zip of five small XML files, and zip permits
 * stored (uncompressed) entries, so the whole writer is the CRC and the
 * headers - about a hundred lines, and no supply chain.
 *
 * Columns are declared as { key, label, type } where type is 'number',
 * 'money' or omitted for text. The type decides how Excel is told to treat
 * the cell, so a column of hours can be summed in the sheet rather than
 * arriving as text that looks like a number.
 */

/* ============================== CSV ==================================== */

/**
 * Excel executes a cell that begins = + - or @, whatever the file extension.
 * A ticket title someone typed as "=2+3" is harmless; one typed as
 * =HYPERLINK(...) or a DDE payload is not, and a contractor opening a
 * downloaded timesheet is exactly the moment it would run. Prefixing an
 * apostrophe keeps the text visible and stops it being read as a formula.
 */
function csvSafe(value) {
  const text = value === null || value === undefined ? '' : String(value);
  if (!/^[=+\-@\t\r]/.test(text)) return text;
  // A plain negative number is not the dangerous case - "-10" is a figure,
  // while "-2+3+cmd|' /C calc'!A0" is the attack. Escaping both put the
  // variance column into Excel as text, which is useless to anyone who wants
  // to add it up, so a value that is simply a number is left alone.
  if (/^-?\d+(\.\d+)?$/.test(text)) return text;
  return `'${text}`;
}

function csvCell(value) {
  const text = csvSafe(value);
  // Quote when it could otherwise break the row, and double any quote inside.
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv(columns, rows) {
  const head = columns.map((c) => csvCell(c.label)).join(',');
  const body = rows.map((row) =>
    columns.map((c) => csvCell(format(row[c.key], c, 'csv'))).join(','));
  // CRLF and a byte-order mark: without the BOM Excel on Windows reads the
  // file as the local codepage and turns every accented character to mojibake.
  return `﻿${[head, ...body].join('\r\n')}\r\n`;
}

/* ============================== values ================================= */

function format(value, column, target) {
  if (value === null || value === undefined || value === '') return '';
  if (column.type === 'number' || column.type === 'money') {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    // Left as a number for both targets; the currency symbol belongs to the
    // screen, not to a cell somebody wants to add up.
    return target === 'xlsx' ? n : String(n);
  }
  if (column.type === 'date') return String(value).slice(0, 10);
  return String(value);
}

/* ============================== XLSX =================================== */

const xmlEscape = (s) => String(s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&apos;')
  // Excel refuses to open a file containing control characters XML cannot
  // represent - it reports the whole workbook as corrupt rather than skipping
  // the cell - so they are stripped. Matching control characters is the entire
  // point here, which is what the rule below is warning about.
  // eslint-disable-next-line no-control-regex
  .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');

/** A1, B1 … AA1. Needed because a cell without a ref is a corrupt sheet. */
function cellRef(colIndex, rowNumber) {
  let n = colIndex + 1;
  let name = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    name = String.fromCharCode(65 + rem) + name;
    n = Math.floor((n - 1) / 26);
  }
  return `${name}${rowNumber}`;
}

function sheetXml(columns, rows) {
  const lines = [];
  lines.push('<row r="1">' + columns.map((c, i) =>
    `<c r="${cellRef(i, 1)}" t="inlineStr" s="1"><is><t>${xmlEscape(c.label)}</t></is></c>`
  ).join('') + '</row>');

  rows.forEach((row, ri) => {
    const r = ri + 2;
    const cells = columns.map((c, ci) => {
      const v = format(row[c.key], c, 'xlsx');
      const ref = cellRef(ci, r);
      if (v === '') return '';
      if (typeof v === 'number') {
        const style = c.type === 'money' ? ' s="2"' : '';
        return `<c r="${ref}"${style}><v>${v}</v></c>`;
      }
      // inlineStr rather than a shared string table: one fewer file to get
      // right, and these sheets are small enough that the saving is nothing.
      return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${xmlEscape(v)}</t></is></c>`;
    }).join('');
    lines.push(`<row r="${r}">${cells}</row>`);
  });

  const widths = columns.map((c, i) =>
    `<col min="${i + 1}" max="${i + 1}" width="${c.width || 16}" customWidth="1"/>`).join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<cols>${widths}</cols>
<sheetData>${lines.join('')}</sheetData>
</worksheet>`;
}

/**
 * The files of a workbook with one sheet per entry of `sheets`
 * ([{ name, columns, rows }]). Excel refuses a name longer than 31
 * characters, one with any of \ / ? * [ ] :, and two sheets with the same
 * name - so names are cleaned, cut and, when two collide, numbered.
 */
function workbookFiles(sheets) {
  const used = new Set();
  const names = sheets.map((sh, i) => {
    const clean = String(sh.name || `Sheet${i + 1}`).replace(/[\\/?*[\]:]/g, ' ').trim().slice(0, 31) || `Sheet${i + 1}`;
    let name = clean;
    for (let n = 2; used.has(name.toLowerCase()); n += 1) name = `${clean.slice(0, 31 - String(n).length - 1)} ${n}`;
    used.add(name.toLowerCase());
    return name;
  });
  const n = sheets.length;
  const each = (fn) => sheets.map((sh, i) => fn(i + 1, names[i])).join('\n');
  return [
    ['[Content_Types].xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
${each((k) => `<Override PartName="/xl/worksheets/sheet${k}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`)}
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`],
    ['_rels/.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`],
    ['xl/workbook.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets>${each((k, name) => `<sheet name="${xmlEscape(name)}" sheetId="${k}" r:id="rId${k}"/>`)}</sheets>
</workbook>`],
    ['xl/_rels/workbook.xml.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${each((k) => `<Relationship Id="rId${k}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${k}.xml"/>`)}
<Relationship Id="rId${n + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`],
    // Three styles: plain, bold for the header row, and two decimal places
    // for money so an amount does not arrive as 1234.5.
    ['xl/styles.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="1"><numFmt numFmtId="164" formatCode="0.00"/></numFmts>
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="3">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`],
    ...sheets.map((sh, i) => [`xl/worksheets/sheet${i + 1}.xml`, sheetXml(sh.columns, sh.rows)])
  ];
}

/* ------------------------------- zip ----------------------------------- */

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i += 1) {
    c = CRC_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
  }
  return (c ^ 0xFFFFFFFF) >>> 0;
}

/**
 * A zip with stored (method 0) entries.
 *
 * Uncompressed because compressing would mean shipping a DEFLATE
 * implementation, and every tool that opens an xlsx reads stored entries
 * quite happily. A sheet of a few hundred rows is tens of kilobytes either
 * way.
 */
function zip(files) {
  const encoder = new TextEncoder();
  const chunks = [];
  const central = [];
  let offset = 0;

  const u16 = (n) => [n & 0xFF, (n >>> 8) & 0xFF];
  const u32 = (n) => [n & 0xFF, (n >>> 8) & 0xFF, (n >>> 16) & 0xFF, (n >>> 24) & 0xFF];

  for (const [name, content] of files) {
    const nameBytes = encoder.encode(name);
    const data = encoder.encode(content);
    const sum = crc32(data);

    const local = [
      ...u32(0x04034b50), ...u16(20), ...u16(0), ...u16(0),
      ...u16(0), ...u16(0),                 // time, date
      ...u32(sum), ...u32(data.length), ...u32(data.length),
      ...u16(nameBytes.length), ...u16(0)
    ];
    chunks.push(new Uint8Array(local), nameBytes, data);

    central.push([
      ...u32(0x02014b50), ...u16(20), ...u16(20), ...u16(0), ...u16(0),
      ...u16(0), ...u16(0),
      ...u32(sum), ...u32(data.length), ...u32(data.length),
      ...u16(nameBytes.length), ...u16(0), ...u16(0), ...u16(0), ...u16(0),
      ...u32(0), ...u32(offset),
      ...Array.from(nameBytes)
    ]);

    offset += local.length + nameBytes.length + data.length;
  }

  const dir = central.flat();
  const end = [
    ...u32(0x06054b50), ...u16(0), ...u16(0),
    ...u16(files.length), ...u16(files.length),
    ...u32(dir.length), ...u32(offset), ...u16(0)
  ];

  chunks.push(new Uint8Array(dir), new Uint8Array(end));
  return new Blob(chunks, {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
}

export function toXlsx(columns, rows, sheetName) {
  return zip(workbookFiles([{ name: sheetName || 'Sheet1', columns, rows }]));
}

/** A workbook of several sheets: [{ name, columns, rows }] - the monthly report's pack. */
export function toXlsxBook(sheets) {
  return zip(workbookFiles(sheets.length ? sheets : [{ name: 'Sheet1', columns: [], rows: [] }]));
}

/* ============================== download =============================== */

function save(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on the next tick: revoking immediately cancels the download in
  // some browsers before it has started reading the blob.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** A filename that sorts by date and says what it is. */
export function exportName(base, extension) {
  const stamp = new Date().toISOString().slice(0, 10);
  return `${base}-${stamp}.${extension}`;
}

export function downloadCsv(columns, rows, base) {
  save(new Blob([toCsv(columns, rows)], { type: 'text/csv;charset=utf-8' }),
    exportName(base, 'csv'));
}

export function downloadXlsx(columns, rows, base, sheetName) {
  save(toXlsx(columns, rows, sheetName || base), exportName(base, 'xlsx'));
}

/**
 * Several tables, one file: `sheets` is [{ name, columns, rows }]. `name`
 * is the whole filename when it is given (the report names its own by its
 * month, not by today's date).
 */
export function downloadXlsxBook(sheets, base, name = null) {
  save(toXlsxBook(sheets), name || exportName(base, 'xlsx'));
}

/** One CSV, with `name` as the whole filename when it is given. */
export function downloadCsvNamed(columns, rows, name) {
  save(new Blob([toCsv(columns, rows)], { type: 'text/csv;charset=utf-8' }), name);
}
